/**
 * Sélecteur de période partagé (BUG-1, ANOM-3, D-01, D-03).
 *
 * - `allowFuture` est une prop EXPLICITE, sans valeur par défaut : chaque
 *   écran dit s'il accepte le futur. Le monitoring passe `true` (D-01 : une
 *   période future montre les stocks « au » fin de période, par exemple les
 *   abonnements qui vont expirer).
 * - Préréglages calendaires (D-03). Le préréglage actif est porté par l'état
 *   (`preset`), jamais déduit de la longueur de la plage.
 * - « Aujourd'hui » est `today`, fourni par le serveur (`GET /scopes`,
 *   contrat v2.2 §11) : jamais l'horloge ni le fuseau du navigateur. Tant
 *   qu'il n'est pas connu, les préréglages sont en attente (aucun calcul
 *   local) ; « Actualiser » recharge `/scopes`, donc `today`.
 */
import React from 'react';
import { Button, DatePicker, Space, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import { PRESET_KEYS, computePreset, isIsoDate, type DateRange, type PresetKey } from './periods';

const { RangePicker } = DatePicker;
const ISO = 'YYYY-MM-DD';

export type PeriodPickerProps = {
    value: DateRange;
    preset: PresetKey | null;
    onChange: (range: DateRange, preset: PresetKey | null) => void;
    /** Autoriser les dates futures. Obligatoire : aucun choix implicite. */
    allowFuture: boolean;
    /** Jour courant du fuseau métier (`today` de `/scopes`) ; `null` tant qu'il n'est pas reçu. */
    today: string | null;
};

const PeriodPicker: React.FC<PeriodPickerProps> = ({ value, preset, onChange, allowFuture, today }) => {
    const { t } = useTranslation();

    // Sans `today`, aucune date n'est réputée passée : tout est grisé.
    const disabledDate = allowFuture
        ? undefined
        : (current: Dayjs) => !!current && (today === null || current.format(ISO) > today);

    const hasValue = isIsoDate(value.from) && isIsoDate(value.to);

    return (
        <Space size={8} wrap>
            <RangePicker
                allowClear={false}
                value={hasValue ? [dayjs(value.from, ISO), dayjs(value.to, ISO)] : null}
                format="DD/MM/YYYY"
                disabledDate={disabledDate}
                aria-label={t('monitoring.period.range')}
                onChange={(values) => {
                    if (values && values[0] && values[1]) {
                        // Plage choisie à la main : aucun préréglage actif.
                        onChange({ from: values[0].format(ISO), to: values[1].format(ISO) }, null);
                    }
                }}
            />
            <Space
                size={4}
                wrap
                role="group"
                aria-label={t('monitoring.period.presets')}
                aria-busy={today === null}
                data-today={today ?? undefined}
            >
                {today === null && (
                    <span role="status" data-testid="period-today-loading" aria-label={t('monitoring.period.waitingToday')}>
                        <Spin size="small" />
                    </span>
                )}
                {PRESET_KEYS.map((key) => (
                    <Button
                        key={key}
                        size="small"
                        type={preset === key ? 'primary' : 'default'}
                        aria-pressed={preset === key}
                        disabled={today === null}
                        onClick={() => {
                            if (today !== null) onChange(computePreset(key, today), key);
                        }}
                    >
                        {t(`monitoring.presets.${key}`)}
                    </Button>
                ))}
            </Space>
        </Space>
    );
};

export default PeriodPicker;
